import { SerializedSkipCategoryPreference } from './ParentalControlsService'

export interface PreferenceNode {
  preference: SerializedSkipCategoryPreference
  children: PreferenceNode[]
}

export type MuteStatus = 'muted' | 'unmuted' | 'mixed'

export function buildPreferenceTree(preferences: SerializedSkipCategoryPreference[]): PreferenceNode[] {
  const nodeMap = new Map<number, PreferenceNode>()
  const roots: PreferenceNode[] = []

  for (const pref of preferences) {
    nodeMap.set(pref.id, { preference: pref, children: [] })
  }

  for (const pref of preferences) {
    const node = nodeMap.get(pref.id)
    if (!node) continue
    if (pref.parentId == null) {
      roots.push(node)
    } else {
      const parent = nodeMap.get(pref.parentId)
      if (parent) {
        parent.children.push(node)
      }
    }
  }

  return roots
}

export function getAllNonLeafIds(nodes: PreferenceNode[]): number[] {
  const ids: number[] = []
  for (const node of nodes) {
    if (node.children.length > 0) {
      ids.push(node.preference.id)
      ids.push(...getAllNonLeafIds(node.children))
    }
  }
  return ids
}

/**
 * Count leaf nodes (the actual toggleable items) from a flat preference list.
 * A leaf is a preference whose `id` never appears as another preference's `parentId`.
 */
export function countLeaves(preferences: { id: number; parentId?: number | null }[]): number {
  const parentIds = new Set(preferences.map((p) => p.parentId).filter((id) => id != null))
  return preferences.filter((p) => !parentIds.has(p.id)).length
}

/**
 * Count skipped leaf nodes from a flat preference list.
 */
export function countSkippedLeaves(
  preferences: { id: number; parentId?: number | null; skipped?: boolean | null }[],
): number {
  const parentIds = new Set(preferences.map((p) => p.parentId).filter((id) => id != null))
  return preferences.filter((p) => !parentIds.has(p.id) && p.skipped).length
}

export function getMuteStatus(node: PreferenceNode): MuteStatus {
  if (node.children.length === 0) {
    return node.preference.skipped ? 'muted' : 'unmuted'
  }
  const { skippedCount, totalCount } = node.preference
  if (skippedCount === totalCount && totalCount > 0) return 'muted'
  if (skippedCount === 0) return 'unmuted'
  return 'mixed'
}
